import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconRoute, IconBuildingBank } from '@tabler/icons-react';
import { CrudMode, CrudSchemaField, FormItemValueType, SelectOptions, type EnsoFormItem } from '@ohpen/enso-ui';
import { Button, Form } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

import { TabProps } from '../interfaces';
import { FieldType } from '@pmp/api';
import { DEFAULT_TEXT_MAX_LENGTH_MEDIUM, textLimitProps } from '@pmp/utils';
import { MANDATORY_BANK_OPERATIONS, OPERATION_ORDER, cleanupRemovedOperations, getPaymentRouteOptions } from '../utils';

export const Payments: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const watchedOperations = Form.useWatch(['registrationDetails', 'operations'], form);
  // preserve: true is required because the bank detail fields are only mounted once an
  // operation has details to show, and useWatch otherwise only sees registered fields.
  const watchedPaymentOps = Form.useWatch(['payments', 'operations'], { form, preserve: true });
  const seededRef = useRef(false);

  const operations: string[] = useMemo(() => {
    if (!watchedOperations) return [];
    return [...watchedOperations].sort((a, b) => OPERATION_ORDER.indexOf(a) - OPERATION_ORDER.indexOf(b));
  }, [watchedOperations]);

  const [bankDetailsVisible, setBankDetailsVisible] = useState<Set<string>>(new Set());

  const hasSavedBankDetails = useCallback(
    (operation: string): boolean => {
      const data = watchedPaymentOps?.[operation];
      return !!(data?.accountName || data?.sortCode || data?.accountNumber || data?.bankName);
    },
    [watchedPaymentOps],
  );

  useEffect(() => {
    if (seededRef.current || !watchedPaymentOps) return;
    seededRef.current = true;
    const visible = new Set(Object.keys(watchedPaymentOps).filter(hasSavedBankDetails));
    if (visible.size > 0) {
      setBankDetailsVisible(visible);
    }
  }, [watchedPaymentOps, hasSavedBankDetails]);

  const prevOperationsRef = useRef<string[]>([]);

  useEffect(() => {
    cleanupRemovedOperations(prevOperationsRef, operations, form, setBankDetailsVisible);
  }, [operations, form, setBankDetailsVisible]);

  const addBankDetails = useCallback((operation: string) => {
    setBankDetailsVisible(prev => new Set(prev).add(operation));
  }, []);

  const removeBankDetails = useCallback((operation: string) => {
    setBankDetailsVisible(prev => {
      const next = new Set(prev);
      next.delete(operation);
      return next;
    });
    form?.setFieldValue(['payments', 'operations', operation], undefined);
  }, [form]);

  const watchedPaymentRoutes = Form.useWatch(['payments', 'paymentRouteFirmIds'], form);
  const watchedDefaultPaymentRoute = Form.useWatch(['payments', 'defaultPaymentRouteFirmId'], form);
  const selectedPaymentRoutes: string[] = useMemo(() => watchedPaymentRoutes ?? [], [watchedPaymentRoutes]);

  const paymentRouteOptions: SelectOptions = useMemo(() => getPaymentRouteOptions(selectsOptions, t), [selectsOptions, t]);

  const defaultPaymentRouteOptions: SelectOptions = useMemo(
    () => paymentRouteOptions.filter(option => selectedPaymentRoutes.includes(option.value)),
    [paymentRouteOptions, selectedPaymentRoutes],
  );

  useEffect(() => {
    if (readonly) return;
    const keepsCurrent = !!watchedDefaultPaymentRoute && selectedPaymentRoutes.includes(watchedDefaultPaymentRoute);
    const next = keepsCurrent ? watchedDefaultPaymentRoute : (selectedPaymentRoutes.length === 1 ? selectedPaymentRoutes[0] : undefined);
    if ((next ?? null) !== (watchedDefaultPaymentRoute ?? null)) {
      // setFieldValue leaves the required error antd already reported standing, because a value
      // written for the user triggers no revalidation; setFields drops it along with the value.
      form?.setFields([{ name: ['payments', 'defaultPaymentRouteFirmId'], value: next, errors: [] }]);
    }
  }, [readonly, selectedPaymentRoutes, watchedDefaultPaymentRoute, form]);

  const bankDetailFields = useCallback(
    (operation: string): EnsoFormItem[] => [
      {
        title: t('lender.firms.labels.accountName'),
        dataIndex: ['payments', 'operations', operation, 'accountName'],
        valueType: FormItemValueType.text,
        readonly,
        fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
        formItemProps: { required: true },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.sortCode'),
        dataIndex: ['payments', 'operations', operation, 'sortCode'],
        valueType: FormItemValueType.text,
        readonly,
        fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
        formItemProps: { required: true },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.accountNumber'),
        dataIndex: ['payments', 'operations', operation, 'accountNumber'],
        valueType: FormItemValueType.text,
        readonly,
        fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
        formItemProps: { required: true },
        colProps: { span: 6 },
      },
      {
        title: t('lender.firms.labels.bankName'),
        dataIndex: ['payments', 'operations', operation, 'bankName'],
        valueType: FormItemValueType.text,
        readonly,
        fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_MEDIUM),
        colProps: { span: 6 },
      },
    ],
    [readonly, t],
  );

  const accountColumns: EnsoFormItem[] = useMemo(
    () =>
      operations
        .filter(operation => !readonly || hasSavedBankDetails(operation))
        .map(operation => {
          const bankMandatory = MANDATORY_BANK_OPERATIONS.has(operation);
          const hasBank = readonly || bankDetailsVisible.has(operation) || bankMandatory;
          const operationLabel = selectsOptions[FieldType.OPERATION_TYPE]?.find(opt => opt.value === operation)?.label;

          return {
            title: `${t('lender.firms.labels.accountFor')} ${operationLabel ?? operation}`,
            valueType: FormItemValueType.group,
            readonly,
            gutter: [24, 0],
            columns: hasBank
              ? [
                ...bankDetailFields(operation),
                ...(!readonly && !bankMandatory
                  ? [
                    {
                      renderFormItem: () => (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => removeBankDetails(operation)}
                        >
                          {t('global.buttons.delete')}
                        </Button>
                      ),
                      colProps: { span: 24 },
                    },
                  ]
                  : []),
              ]
              : [
                {
                  renderFormItem: () => (
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => addBankDetails(operation)}
                    >
                      {t('global.buttons.add')}
                    </Button>
                  ),
                  colProps: { span: 24 },
                },
              ],
          };
        }),
    [selectsOptions, operations, readonly, t, bankDetailsVisible, hasSavedBankDetails, bankDetailFields, addBankDetails, removeBankDetails],
  );

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.firms.labels.paymentRoutes'),
            dataIndex: ['payments', 'paymentRouteFirmIds'],
            valueType: FormItemValueType.select,
            readonly,
            readonlyDisplay: 'tag',
            fieldProps: {
              optionFilterProp: 'label',
              mode: 'multiple',
              options: paymentRouteOptions,
              placeholder: t('lender.firms.labels.paymentRoutesPlaceholder'),
            },
            formItemProps: {
              required: true,
            },
            colProps: { span: 24 },
          },
          {
            title: t('lender.firms.labels.defaultPaymentRoute'),
            dataIndex: ['payments', 'defaultPaymentRouteFirmId'],
            valueType: FormItemValueType.select,
            readonly,
            readonlyDisplay: 'tag',
            fieldProps: {
              optionFilterProp: 'label',
              showSearch: true,
              options: defaultPaymentRouteOptions,
              disabled: selectedPaymentRoutes.length === 0,
              placeholder: selectedPaymentRoutes.length > 0
                ? t('lender.firms.labels.defaultPaymentRoutePlaceholder')
                : t('lender.firms.labels.selectPaymentRoutesFirst'),
            },
            formItemProps: {
              required: true,
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [readonly, t, paymentRouteOptions, defaultPaymentRouteOptions, selectedPaymentRoutes],
  );

  const schema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconRoute />,
      title: t('lender.firms.labels.payments'),
      columns,
    }),
    [columns, t],
  );

  const bankAccountsSchema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconBuildingBank />,
      title: t('lender.firms.labels.bankAccounts'),
      columns: accountColumns,
    }),
    [accountColumns, t],
  );

  return (
    <>
      <CrudSchemaField item={schema} form={form} readonly={readonly} />
      {accountColumns.length > 0 && <CrudSchemaField item={bankAccountsSchema} form={form} readonly={readonly} />}
    </>
  );
};
